import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { ContentFitnessSubcriterion } from "contracts/report/content-fitness/subcriteria";
import { createMemo, For, Show } from "solid-js";
import { spacedKey } from "../../boundaries/label.formatter";
import type { ContentFitnessOrigin } from "../../enums/scan";
import { pluralize } from "../../ui/format";
import {
  pageDisplayLabel,
  rawPagePath,
} from "../pages/boundaries/page-label.parser";
import { verificationStateLabel } from "./clientLanguage";
import {
  type AssessmentRow,
  rowsForSubcriterion,
} from "./contentFitnessDimensionModel";
import { clientDisplayText, presentEvidence } from "./evidencePresentation";
import { pageDetailHref } from "./pageDetailNavigation";
import { EmptyState } from "./ScanContentFitnessParts";

type Site = OverviewResponse["site"];

export function SubcriterionAssessmentDetails(props: {
  subcriteria: readonly ContentFitnessSubcriterion[];
  rows: AssessmentRow[];
  loading: boolean;
  site: Site;
  websiteId: string;
  dimensionKey: string;
  origin: ContentFitnessOrigin;
}) {
  const assessed = createMemo(() =>
    props.subcriteria.filter(
      (item) => rowsForSubcriterion(props.rows, item.key).length > 0,
    ),
  );
  return (
    <section class="scan-panel">
      <div class="scan-section-head">
        <div>
          <span class="scan-kicker">Detailed rationale and evidence</span>
          <h2>Page assessments</h2>
          <p class="scan-muted">
            Open a subcriterion to review its page-level rationale. Evidence and
            implementation details remain available one level deeper.
          </p>
        </div>
      </div>
      <Show
        when={assessed().length > 0}
        fallback={
          <EmptyState
            loading={props.loading}
            text="No persisted page assessments were found for this dimension."
          />
        }
      >
        <div class="scan-subcriterion-disclosures">
          <For each={assessed()}>
            {(subcriterion) => (
              <SubcriterionDisclosure
                subcriterion={subcriterion}
                rows={rowsForSubcriterion(props.rows, subcriterion.key)}
                site={props.site}
                websiteId={props.websiteId}
                dimensionKey={props.dimensionKey}
                origin={props.origin}
              />
            )}
          </For>
        </div>
      </Show>
    </section>
  );
}

function SubcriterionDisclosure(props: {
  subcriterion: ContentFitnessSubcriterion;
  rows: AssessmentRow[];
  site: Site;
  websiteId: string;
  dimensionKey: string;
  origin: ContentFitnessOrigin;
}) {
  return (
    <details class="scan-subcriterion-disclosure">
      <summary>
        <span>
          <strong>{props.subcriterion.label}</strong>
          <small>{props.subcriterion.definition}</small>
        </span>
        <b>
          {props.rows.length} {pluralize(props.rows.length, "page assessment")}
        </b>
      </summary>
      <div class="scan-subcriterion-pages">
        <For each={props.rows}>
          {(row) => (
            <PageAssessment
              row={row}
              site={props.site}
              websiteId={props.websiteId}
              dimensionKey={props.dimensionKey}
              origin={props.origin}
            />
          )}
        </For>
      </div>
    </details>
  );
}

function PageAssessment(props: {
  row: AssessmentRow;
  site: Site;
  websiteId: string;
  dimensionKey: string;
  origin: ContentFitnessOrigin;
}) {
  const evidence = () => presentEvidence(props.row.assessment.evidence);
  return (
    <article class="scan-assessment-row scan-page-assessment">
      <div>
        <a
          class="scan-table-link"
          href={pageDetailHref(props.websiteId, props.row.page.id, {
            source: "content-fitness",
            dimensionKey: props.dimensionKey,
            dimensionOrigin: props.origin,
          })}
        >
          <strong>{pageDisplayLabel(props.row.page, props.site)}</strong>
        </a>
        <small class="mono">{rawPagePath(props.row.page)}</small>
      </div>
      <div class="scan-assessment-score">
        <strong class="mono">{props.row.assessment.score ?? "--"}/4</strong>
        <small>
          {verificationStateLabel(props.row.assessment.verificationState)}
        </small>
      </div>
      <p>
        {clientDisplayText(props.row.assessment.rationale) ||
          "No rationale was persisted."}
      </p>
      <EvidenceDisclosure label="Evidence" evidence={evidence().readable} />
      <details class="scan-inline-disclosure scan-technical-detail">
        <summary>Technical detail</summary>
        <div class="scan-disclosure-body">
          <dl class="scan-facts">
            <div class="scan-fact">
              <dt>Canonical subcriterion key</dt>
              <dd class="mono">{props.row.assessment.subcriterionKey}</dd>
            </div>
            <div class="scan-fact">
              <dt>Assessment mode</dt>
              <dd>{props.row.subcriterion.assessmentMode}</dd>
            </div>
          </dl>
          <EvidenceDisclosure
            label="Selectors and raw expressions"
            evidence={evidence().technical}
          />
        </div>
      </details>
    </article>
  );
}

function EvidenceDisclosure(props: {
  label: string;
  evidence: Array<{ kind?: string | null; value: string }>;
}) {
  return (
    <Show when={props.evidence.length > 0}>
      <details class="scan-inline-disclosure scan-assessment-evidence">
        <summary>
          {props.label} ({props.evidence.length})
        </summary>
        <ul>
          <For each={props.evidence}>
            {(item) => (
              <li>
                <strong>{evidenceLabel(item.kind)}</strong>
                <span>{item.value}</span>
              </li>
            )}
          </For>
        </ul>
      </details>
    </Show>
  );
}

function evidenceLabel(kind?: string | null): string {
  if (!kind) return "Evidence";
  return kind.charAt(0).toUpperCase() + spacedKey(kind.slice(1));
}
